import React, { useState, useEffect } from "react";
import {
  AiOutlineUserAdd,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlineSearch,
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineFileExcel,
} from "react-icons/ai";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Input,
  Button,
  Label,
  FormGroup,
  Pagination,
  PaginationItem,
  PaginationLink,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from "reactstrap";
import { createAdmin, getAdmins, updateAdmin, deleteAdmin, toggleAdminStatus, getAdminById } from "../../services/api";
import "../../../styles/dashboard/SuperAdmin/AdminManagement.css";
import "../../../styles/dashboard/OrgUser/CreditCheck.css";
import * as XLSX from "xlsx";

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [filteredAdmins, setFilteredAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const adminsPerPage = 10;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(""); // add, edit, delete, view
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    role: "Admin",
    username: "",
    password: "",
    address: "",
    aadhaarNumber: "",
    panNumber: "",
    employeeId: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");

  // FETCH ADMINS
  useEffect(() => {
    const fetchAdmins = async () => {
      const result = await getAdmins(searchTerm);
      if (result.success) {
        const mapped = result.data.data.map((a) => ({
          id: a.id,
          fullName: a.fullName,
          email: a.email,
          mobileNumber: a.mobileNumber || "",
          role: a.role === "superadmin" ? "Super Admin" : "Admin",
          username: a.username,
          status: a.isActive ? "Active" : "Inactive",
          employeeId: a.employeeId || "",
          isActive: a.isActive,
          address: a.address || "",
          aadhaarNumber: a.aadhaarNumber || "",
          panNumber: a.panNumber || "",
          createdAt: a.createdAt ? new Date(a.createdAt).toLocaleString() : "-",
          updatedAt: a.updatedAt ? new Date(a.updatedAt).toLocaleString() : "-",
        }));
        setAdmins(mapped);
      } else {
        showToast("Failed to load admins: " + result.error, "danger");
      }
    };
    fetchAdmins();
  }, [searchTerm]);

  // SEARCH & FILTER
  useEffect(() => {
    let filtered = admins.filter(
      (a) =>
        a.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        a.username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((a) => a.status === filterStatus);
    }

    // Apply date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter((a) => {
        const createdDate = new Date(a.createdAt);
        return createdDate >= fromDate;
      });
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter((a) => {
        const createdDate = new Date(a.createdAt);
        return createdDate <= toDate;
      });
    }

    setFilteredAdmins(filtered);
    setCurrentPage(1);
  }, [searchTerm, filterStatus, dateFrom, dateTo, admins]);

  // TOAST
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  // PAGINATION
  const indexOfLast = currentPage * adminsPerPage;
  const indexOfFirst = indexOfLast - adminsPerPage;
  const currentAdmins = filteredAdmins.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredAdmins.length / adminsPerPage);

  // VALIDATION
  const validateField = (name, value, allValues = {}) => {
    let error = "";
    const val = (value || "").toString().trim();

    switch (name) {
      case "fullName":
        if (!val) error = "Full Name is required";
        else if (val.length < 2) error = "Name must be at least 2 characters";
        break;
      case "username":
        if (!val) error = "Username is required";
        else if (!/^[a-zA-Z0-9._-]{3,}$/.test(val))
          error = "Min 3 chars, only letters, numbers, . _ -";
        break;
      case "email":
        if (!val) error = "Email is required";
        else if (!/^\S+@\S+\.\S+$/.test(val)) error = "Invalid email format";
        break;
      case "mobileNumber":
        if (val && !/^\d{10}$/.test(val.replace(/[-\s]/g, "")))
          error = "Mobile must be 10 digits";
        break;
      case "aadhaarNumber":
        if (val && !/^\d{12}$/.test(val)) error = "Aadhaar must be 12 digits";
        break;
      case "panNumber":
        if (val && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val))
          error = "Invalid PAN (e.g. ABCDE1234F)";
        break;
      case "password":
        if (modalMode === "add" && !val) error = "Password is required";
        else if (val && val.length < 6) error = "Password must be 6+ characters";
        break;
      default:
        break;
    }
    return error;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "employeeId") return;

    const normalizedValue = name === "panNumber" ? value.toUpperCase() : value;
    const updatedForm = { ...formData, [name]: normalizedValue };
    setFormData(updatedForm);

    const fieldError = validateField(name, normalizedValue, updatedForm);
    setErrors((prev) => {
      const newErr = { ...prev };
      if (fieldError) newErr[name] = fieldError;
      else delete newErr[name];
      return newErr;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    ["fullName", "email", "username"].forEach((f) => {
      const err = validateField(f, formData[f], formData);
      if (err) newErrors[f] = err;
    });

    if (modalMode === "add") {
      if (!formData.password) newErrors.password = "Password is required";
      else if (formData.password.length < 6) newErrors.password = "Password must be 6+ characters";
    }

    ["mobileNumber", "aadhaarNumber", "panNumber"].forEach((f) => {
      const err = validateField(f, formData[f], formData);
      if (err) newErrors[f] = err;
    });

    if (modalMode === "add") {
      const emailTaken = admins.some(
        (a) => a.email.toLowerCase() === formData.email.toLowerCase()
      );
      const userTaken = admins.some(
        (a) => a.username.toLowerCase() === formData.username.toLowerCase()
      );
      if (emailTaken) newErrors.email = "Email already exists";
      if (userTaken) newErrors.username = "Username already taken";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateEmployeeId = () => {
    if (admins.length === 0) return "EMP0001";
    const empNumbers = admins
      .map((a) => {
        const match = a.employeeId?.match(/^EMP(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((n) => n > 0);
    const maxNum = empNumbers.length > 0 ? Math.max(...empNumbers) : 0;
    const nextNum = maxNum + 1;
    return `EMP${nextNum.toString().padStart(4, "0")}`;
  };

  // MODAL OPEN
  const openModal = async (mode, admin = null) => {
    setModalMode(mode);
    setSelectedAdmin(admin);
    setErrors({});
    setApiError("");
    setShowPassword(false);

    if (mode === "add") {
      const autoEmpId = generateEmployeeId();
      setFormData({
        fullName: "",
        email: "",
        mobileNumber: "",
        role: "Admin",
        username: "",
        password: "",
        address: "",
        aadhaarNumber: "",
        panNumber: "",
        employeeId: autoEmpId,
      });
    } else if (mode === "edit" && admin) {
      const result = await getAdminById(admin.id);
      if (result.success) {
        const fresh = result.data;
        setFormData({
          fullName: fresh.fullName || "",
          email: fresh.email || "",
          mobileNumber: fresh.mobileNumber || "",
          role: fresh.role === "superadmin" ? "Super Admin" : "Admin",
          username: fresh.username || "",
          password: "",
          address: fresh.address || "",
          aadhaarNumber: fresh.aadhaarNumber || "",
          panNumber: fresh.panNumber || "",
          employeeId: fresh.employeeId || "",
        });
      }
    } else if (mode === "view" && admin) {
      const result = await getAdminById(admin.id);
      if (result.success) {
        setSelectedAdmin(result.data);
      }
    } else if (mode === "delete" && admin) {
      // Just open
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMode("");
    setSelectedAdmin(null);
    setErrors({});
    setApiError("");
    setShowPassword(false);
  };

  // SUBMIT
  const handleSubmit = async () => {
    if (!validateForm()) {
      showToast("Please fix the errors below", "danger");
      return;
    }

    setIsSubmitting(true);
    setApiError("");

    const apiRole = formData.role === "Super Admin" ? "superadmin" : "admin";

    const payload = {
      fullName: formData.fullName.trim(),
      email: formData.email.trim(),
      mobileNumber: formData.mobileNumber || null,
      role: apiRole,
      username: formData.username.trim(),
      password: modalMode === "add" ? formData.password : undefined,
      address: formData.address || null,
      aadhaarNumber: formData.aadhaarNumber || null,
      panNumber: formData.panNumber || null,
      employeeId: formData.employeeId || null,
    };

    try {
      if (modalMode === "add") {
        const result = await createAdmin(payload);
        if (result.success) {
          const newAdmin = { ...payload, id: result.data.id, status: "Active", isActive: true };
          setAdmins((prev) => [...prev, newAdmin]);
          showToast("Admin created successfully!", "success");
          closeModal();
        } else {
          setApiError(result.error || "Failed to create admin");
          showToast(result.error || "Failed to create admin", "danger");
        }
      } else if (modalMode === "edit") {
        const result = await updateAdmin(selectedAdmin.id, payload);
        if (result.success) {
          setAdmins((prev) =>
            prev.map((a) =>
              a.id === selectedAdmin.id
                ? { ...a, ...payload, role: formData.role }
                : a
            )
          );
          showToast("Admin updated!", "success");
          closeModal();
        } else {
          setApiError(result.error || "Update failed");
          showToast(result.error || "Update failed", "danger");
        }
      }
    } catch (err) {
      setApiError("Server error. Please try again.");
      // showKonwToast("Server error", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const result = await deleteAdmin(selectedAdmin.id);
    if (result.success) {
      setAdmins((prev) => prev.filter((a) => a.id !== selectedAdmin.id));
      showToast("Admin deleted!", "success");
      closeModal();
    } else {
      showToast(result.error || "Failed to delete admin", "danger");
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    const isActive = newStatus === "Active";
    const result = await toggleAdminStatus(id, isActive);
    if (result.success) {
      setAdmins((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: newStatus, isActive } : a))
      );
      showToast("Status updated", "success");
    } else {
      showToast(result.error || "Failed to update status", "danger");
    }
  };

  // Export to Excel
  const handleExportToExcel = () => {
    const exportData = currentAdmins.map((admin, idx) => ({
      "Sr No.": indexOfFirst + idx + 1,
      "Full Name": admin.fullName || "-",
      "Username": admin.username || "-",
      "Email": admin.email || "-",
      "Mobile Number": admin.mobileNumber || "-",
      "Employee ID": admin.employeeId || "-",
      "Address": admin.address || "-",
      "Aadhaar Number": admin.aadhaarNumber || "-",
      "PAN Number": admin.panNumber || "-",
      "Role": admin.role || "-",
      "Status": admin.status || "-",
      "Active": admin.isActive ? "Yes" : "No",
      "Created Date": admin.createdAt ? new Date(admin.createdAt).toLocaleString() : "-",
      "Updated Date": admin.updatedAt ? new Date(admin.updatedAt).toLocaleString() : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    
    // Set column widths for better visibility
    const columnWidths = [
      { wch: 8 },  // Sr No.
      { wch: 20 }, // Full Name
      { wch: 15 }, // Username
      { wch: 25 }, // Email
      { wch: 15 }, // Mobile Number
      { wch: 15 }, // Employee ID
      { wch: 25 }, // Address
      { wch: 15 }, // Aadhaar Number
      { wch: 15 }, // PAN Number
      { wch: 12 }, // Role
      { wch: 12 }, // Status
      { wch: 10 }, // Active
      { wch: 20 }, // Created Date
      { wch: 20 }, // Updated Date
    ];
    worksheet['!cols'] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admins");
    
    const fileName = `Admin_Management_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    showToast("Data exported successfully!", "success");
  };

  return (
    <div className="am-container">
      {/* Toast */}
      {toast.show && (
        <div className={`am-toast am-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <div className="am-toast-progress" />
        </div>
      )}

      <div className="am-header">
        <div>
          <h2>Admin Management</h2>
          <p className="text-muted">Manage admin accounts and permissions</p>
        </div>
      </div>

      <div className="am-controls">
        <div className="am-search-bar">
          <AiOutlineSearch className="am-search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, username..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="am-search-input"
          />
        </div>
      </div>

      <div className="am-filter-row">
        <div className="am-filter-group">
          <div className="am-date-filter">
            <label className="am-filter-label">Date Range:</label>
            <div className="am-date-inputs">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="am-date-input"
                placeholder="From Date"
              />
              <span className="am-date-separator">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="am-date-input"
                placeholder="To Date"
              />
            </div>
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="am-filter-select"
          >
            <option value="all">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>

        <div className="am-action-buttons">
          <button className="am-export-btn" onClick={handleExportToExcel}>
            <AiOutlineFileExcel /> Export
          </button>
          <button className="am-add-btn" onClick={() => openModal("add")}>
            <AiOutlineUserAdd /> Add Admin
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="am-table-wrapper">
        <table className="am-table">
          <thead>
            <tr>
              <th>Sr No.</th>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>Role</th>
              <th>Username</th>
              <th>EMP ID</th>
              <th>Created Date</th>
              <th>Updated Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentAdmins.length === 0 ? (
              <tr>
                <td colSpan="11" className="am-no-data">
                  {admins.length === 0 ? "Loading..." : "No admins found"}
                </td>
              </tr>
            ) : (
              currentAdmins.map((admin, idx) => (
                <tr key={admin.id}>
                  <td>{indexOfFirst + idx + 1}</td>
                  <td className="am-name">{admin.fullName}</td>
                  <td className="am-email">{admin.email}</td>
                  <td>{admin.mobileNumber}</td>
                  <td>
                    <span className={`am-role-badge ${admin.role === "Super Admin" ? "superadmin" : "admin"}`}>
                      {admin.role}
                    </span>
                  </td>
                  <td>{admin.username}</td>
                  <td className="am-emp-id">{admin.employeeId}</td>
                  <td>{admin.createdAt}</td>
                  <td>{admin.updatedAt}</td>
                  <td>
                    <select
                      value={admin.status}
                      onChange={(e) => handleStatusChange(admin.id, e.target.value)}
                      className={`am-status-select ${admin.status.toLowerCase()}`}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </td>
                  <td>
                    <div className="am-actions">
                      <button className="am-action-btn view" onClick={() => openModal("view", admin)} title="View Details">
                        <AiOutlineEye size={18} />
                      </button>
                      <button className="am-action-btn edit" onClick={() => openModal("edit", admin)} title="Edit">
                        <AiOutlineEdit size={18} />
                      </button>
                      <button className="am-action-btn delete" onClick={() => openModal("delete", admin)} title="Delete">
                        <AiOutlineDelete size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="am-pagination-wrapper">
          <div className="am-pagination-info">
            Showing {indexOfFirst + 1} to {Math.min(indexOfLast, filteredAdmins.length)} of {filteredAdmins.length} entries
          </div>
          <div className="am-pagination">
            <button 
              className="am-page-btn" 
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`am-page-btn ${i + 1 === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button 
              className="am-page-btn" 
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="credit-modal-overlay" >
          <div className="credit-modal-content wide" onClick={e => e.stopPropagation()}>
            <div className="credit-modal-header">
              <h2>
                {modalMode === "add" ? "Add Admin" :
                  modalMode === "edit" ? "Edit Admin" :
                    modalMode === "view" ? "Admin Details" :
                      "Delete Admin"}
              </h2>
              <button className="credit-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="credit-modal-body">
          {/* VIEW MODE */}
          {modalMode === "view" && selectedAdmin && (
            <>
              {/* Basic Information */}
              <h6 className="am-category-title">Basic Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Full Name</Label>
                    <Input value={selectedAdmin.fullName || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Username</Label>
                    <Input value={selectedAdmin.username || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Role</Label>
                    <Input value={selectedAdmin.role === "superadmin" ? "Super Admin" : "Admin"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Employee ID</Label>
                    <Input value={selectedAdmin.employeeId || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Address</Label>
                    <Input value={selectedAdmin.address || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Aadhaar Number</Label>
                    <Input value={selectedAdmin.aadhaarNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">PAN Number</Label>
                    <Input value={selectedAdmin.panNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>
              </Row>

              {/* Contact Information */}
              <h6 className="am-category-title">Contact Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Email</Label>
                    <Input value={selectedAdmin.email || "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Mobile Number</Label>
                    <Input autoComplete="new-password"value={selectedAdmin.mobileNumber || "-"} readOnly disabled />
                  </FormGroup>
                </Col>
              </Row>

              {/* System Information */}
              <h6 className="am-category-title">System Information</h6>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Status</Label>
                    <Input value={selectedAdmin.isActive ? "Active" : "Inactive"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Created At</Label>
                    <Input value={selectedAdmin.createdAt ? new Date(selectedAdmin.createdAt).toLocaleString() : "-"} readOnly disabled />
                  </FormGroup>
                </Col>

                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Updated At</Label>
                    <Input value={selectedAdmin.updatedAt ? new Date(selectedAdmin.updatedAt).toLocaleString() : "-"} readOnly disabled />
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}

          {/* DELETE MODE */}
          {modalMode === "delete" && (
            <p>Are you sure you want to delete <strong>{selectedAdmin?.fullName}</strong>?</p>
          )}

          {/* ADD/EDIT MODE */}
          {modalMode !== "view" && modalMode !== "delete" && (
            <>
              {apiError && <div className="alert alert-danger mb-3">{apiError}</div>}

              <Row>
                {/* Full Name */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Full Name <span className="text-danger">*</span></Label>
                    <Input
                      name="fullName"
                      placeholder="Full Name *"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      invalid={!!errors.fullName}
                      className="mb-3"
                    />
                    {errors.fullName && <small className="text-danger">{errors.fullName}</small>}
                  </FormGroup>
                </Col>

                {/* Username */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Username <span className="text-danger">*</span></Label>
                    <Input
                      name="username"
                      placeholder="Username *"
                      value={formData.username}
                      onChange={handleInputChange}
                      invalid={!!errors.username}
                      className="mb-3"
                    />
                    {errors.username && <small className="text-danger">{errors.username}</small>}
                  </FormGroup>
                </Col>

                {/* Email */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Email <span className="text-danger">*</span></Label>
                    <Input
                      name="email"
                      type="email"
                      placeholder="Email *"
                      value={formData.email}
                      onChange={handleInputChange}
                      invalid={!!errors.email}
                      className="mb-3"
                    />
                    {errors.email && <small className="text-danger">{errors.email}</small>}
                  </FormGroup>
                </Col>

                {/* Mobile */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Mobile Number</Label>
                    <Input
                      autoComplete="new-password"
                      name="mobileNumber"
                      placeholder="Mobile (10 digits)"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      invalid={!!errors.mobileNumber}
                      className="mb-3"
                    />
                    {errors.mobileNumber && <small className="text-danger">{errors.mobileNumber}</small>}
                  </FormGroup>
                </Col>

                {/* Role */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Role</Label>
                    <Input
                      type="select"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="mb-3"
                    >
                      <option>Admin</option>
                    </Input>
                  </FormGroup>
                </Col>

                {/* Password (only in Add mode) */}
                {modalMode === "add" && (
                  <Col md="6">
                    <FormGroup>
                      <Label className="am-form-label">Password <span className="text-danger">*</span></Label>
                      <div className="position-relative">
                        <Input
                          autoComplete="new-password"
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="Password *"
                          value={formData.password}
                          onChange={handleInputChange}
                          invalid={!!errors.password}
                          className="mb-3"
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                        </button>
                      </div>
                      {errors.password && <small className="text-danger">{errors.password}</small>}
                    </FormGroup>
                  </Col>
                )}

                {/* Address */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Address</Label>
                    <Input
                      name="address"
                      placeholder="Address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="mb-3"
                    />
                  </FormGroup>
                </Col>

                {/* Aadhaar */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Aadhaar Number</Label>
                    <Input
                      name="aadhaarNumber"
                      placeholder="Aadhaar (12 digits)"
                      value={formData.aadhaarNumber}
                      onChange={handleInputChange}
                      invalid={!!errors.aadhaarNumber}
                      className="mb-3"
                    />
                    {errors.aadhaarNumber && <small className="text-danger">{errors.aadhaarNumber}</small>}
                  </FormGroup>
                </Col>

                {/* PAN */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">PAN Number</Label>
                    <Input
                      name="panNumber"
                      placeholder="PAN (e.g. ABCDE1234F)"
                      value={formData.panNumber}
                      onChange={handleInputChange}
                      invalid={!!errors.panNumber}
                      className="mb-3 am-pan-input"
                    />
                    {errors.panNumber && <small className="text-danger">{errors.panNumber}</small>}
                  </FormGroup>
                </Col>

                {/* Employee ID */}
                <Col md="6">
                  <FormGroup>
                    <Label className="am-form-label">Employee ID</Label>
                    <Input
                      name="employeeId"
                      placeholder="Employee ID (Auto-generated)"
                      value={formData.employeeId}
                      readOnly
                      disabled
                      className="bg-light mb-3"
                    />
                    <small className="text-muted">Auto-generated ID</small>
                  </FormGroup>
                </Col>
              </Row>
            </>
          )}
        </div>

        <div className="credit-modal-footer">
          {modalMode === "delete" ? (
            <>
              <Button color="danger" onClick={handleDelete}>Delete</Button>
              <Button color="secondary" onClick={closeModal}>Cancel</Button>
            </>
          ) : modalMode === "view" ? (
            <Button color="secondary" onClick={closeModal}>Close</Button>
          ) : (
            <Button color="primary" onClick={handleSubmit} disabled={isSubmitting || Object.keys(errors).length > 0}>
              {isSubmitting ? "Saving..." : modalMode === "edit" ? "Update" : "Create"}
            </Button>
          )}
        </div>
      </div>
    </div>
      )}
    </div>
  );
}